import React, { useState } from 'react';
import {
    Card,
    Form,
    DatePicker,
    Radio,
    Button,
    Input,
    Typography,
    Space,
    Row,
    Col,
    Alert,
    Divider,
    Tooltip,
    message,
    Select
} from 'antd';
import { ArrowLeftOutlined, InfoCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { authenticatedKy } from '../../utils';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface FlexibleExportFormData {
    month: dayjs.Dayjs;
    preferences: {
        electricity: string;
        water: string;
        heat: string;
    };
    decimal_separator: 'comma' | 'dot';
    decimal_places: number | null;
    uuids: string;
}

interface ExportResult {
    uuid: string;
    value: number | null;
    timestamp: string | null;
    comment: string;
}

export const FlexibleExport: React.FC = () => {
    const [form] = Form.useForm<FlexibleExportFormData>();
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<ExportResult[]>([]);
    const [parsedUuidCount, setParsedUuidCount] = useState(0);

    const handleUuidChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;

        // Parse UUIDs - preserve line structure but count valid UUIDs (>20 chars)
        const lines = value.split('\n');
        const validUuids = lines
            .map(line => line.trim())
            .filter(uuid => uuid.length >= 20); // Only count UUIDs with 20+ characters

        setParsedUuidCount(validUuids.length);
    };
    
    const handlePaste = (_e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        // Paste handler - no special processing needed
    };

    const handleSubmit = async (values: FlexibleExportFormData) => {
        try {
            setLoading(true);
            
            // Parse UUIDs - preserve line structure for Excel alignment
            const uuidsText = values.uuids || '';
            const lines = uuidsText.split('\n');
            const uuids = lines.map(line => line.trim()); // Keep all values for alignment
            const validUuids = uuids.filter(uuid => uuid.length >= 20); // Count actual valid UUIDs

            if (validUuids.length === 0) {
                message.warning('Please enter at least one valid meter UUID (20+ characters)');
                return;
            }

            // Prepare request payload
            const payload = {
                month: values.month.format('YYYY-MM'),
                preferences: values.preferences,
                decimal_separator: values.decimal_separator,
                decimal_places: values.decimal_places,
                uuids: uuids // Includes empty strings for row alignment
            };

            const response = await authenticatedKy.post('/api/meters/flexible-export/', {
                json: payload,
                timeout: 120000,
            });
            const data = response.ok ? await response.json<{ results: ExportResult[]; success_count: number; error_count: number }>() : null;

            setResults(data?.results || []);
            message.success(`Export completed: ${data?.success_count || 0} successful, ${data?.error_count || 0} errors`);

        } catch (error) {
            message.error('Export failed. Please try again.');
            
            // Mock results for testing when API is not available
            if (error instanceof TypeError && error.message.includes('fetch')) {
                message.warning('Using mock data - API not available');
                const testUuids = (values.uuids || '')
                    .split(/[\s,;]+/)
                    .map(uuid => uuid.trim())
                    .filter(uuid => uuid.length >= 20);
                    
                const mockResults: ExportResult[] = [
                    {
                        uuid: testUuids[0] || 'test-uuid-1',
                        value: 1234.56,
                        timestamp: '2024-06-30T23:59:59Z',
                        comment: 'Last day of month'
                    },
                    {
                        uuid: testUuids[1] || 'test-uuid-2',
                        value: null,
                        timestamp: null,
                        comment: 'Permission denied'
                    }
                ];
                setResults(mockResults);
            }
        } finally {
            setLoading(false);
        }
    };

    const copyResults = (format: 'tsv' | 'csv') => {
        const separator = format === 'tsv' ? '\t' : ';'; // Use semicolon for CSV
        const rows = results.map(result => {
            // If UUID is empty (preserved blank line), create empty row
            if (!result.uuid.trim()) {
                return separator + separator + separator; // Empty cells for alignment
            }
            
            return [
                result.uuid,
                result.value || '',
                result.timestamp ? dayjs(result.timestamp).format('YYYY-MM-DD HH:mm:ss') : '',
                result.comment || ''
            ].join(separator);
        });
        
        const content = rows.join('\n');
        navigator.clipboard.writeText(content);
        message.success(`Copied ${results.length} rows in ${format.toUpperCase()} format (no header, ready for Excel)`);
    };

    if (results.length > 0) {
        return (
            <div style={{ padding: '24px' }}>
                <div style={{ marginBottom: '24px' }}>
                    <Button 
                        icon={<ArrowLeftOutlined />} 
                        onClick={() => setResults([])}
                        style={{ marginBottom: '16px' }}
                    >
                        Back to Form
                    </Button>
                    <Title level={2}>Export Results</Title>
                    <Text type="secondary">
                        {results.length} meter(s) processed. Copy the data below and paste into Excel.
                    </Text>
                </div>

                <Card>
                    <div style={{ marginBottom: '16px' }}>
                        <Space>
                            <Button type="primary" onClick={() => copyResults('tsv')}>
                                Copy for Excel (TSV)
                            </Button>
                            <Button onClick={() => copyResults('csv')}>
                                Copy as CSV
                            </Button>
                        </Space>
                    </div>

                    <div style={{ 
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px', 
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        overflow: 'auto',
                        maxHeight: '400px'
                    }}>
                        {/* Header row */}
                        <div style={{ 
                            display: 'grid',
                            gridTemplateColumns: '200px 100px 150px 1fr',
                            backgroundColor: '#f5f5f5',
                            fontWeight: 'bold',
                            borderBottom: '2px solid #d9d9d9'
                        }}>
                            <div style={{ padding: '8px 12px', borderRight: '1px solid #d9d9d9' }}>UUID</div>
                            <div style={{ padding: '8px 12px', borderRight: '1px solid #d9d9d9' }}>Value</div>
                            <div style={{ padding: '8px 12px', borderRight: '1px solid #d9d9d9' }}>Timestamp</div>
                            <div style={{ padding: '8px 12px' }}>Comment</div>
                        </div>
                        
                        {/* Data rows */}
                        {results.map((result, index) => (
                            <div key={index} style={{ 
                                display: 'grid',
                                gridTemplateColumns: '200px 100px 150px 1fr',
                                minHeight: '32px',
                                opacity: result.uuid.trim() ? 1 : 0.3, // Dim empty lines
                                borderBottom: index < results.length - 1 ? '1px solid #f0f0f0' : 'none'
                            }}>
                                <div style={{ 
                                    padding: '8px 12px', 
                                    borderRight: '1px solid #f0f0f0',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {result.uuid || '\u00A0'}
                                </div>
                                <div style={{ 
                                    padding: '8px 12px', 
                                    borderRight: '1px solid #f0f0f0',
                                    textAlign: 'right'
                                }}>
                                    {result.value || ''}
                                </div>
                                <div style={{ 
                                    padding: '8px 12px', 
                                    borderRight: '1px solid #f0f0f0',
                                    fontSize: '11px'
                                }}>
                                    {result.timestamp ? dayjs(result.timestamp).format('YYYY-MM-DD HH:mm:ss') : ''}
                                </div>
                                <div style={{ 
                                    padding: '8px 12px',
                                    fontSize: '11px',
                                    color: '#666'
                                }}>
                                    {result.comment || ''}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: '32px' }}>
                <Title level={2}>
                    <FileTextOutlined style={{ marginRight: '8px' }} />
                    Flexible Export
                </Title>
                <Paragraph>
                    Export meter data for any list of UUIDs. Perfect for Excel workflows - 
                    paste your meter UUIDs below and get aligned data back.
                </Paragraph>
            </div>

            <Card title="Export Configuration">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        month: dayjs().subtract(1, 'month'),
                        preferences: {
                            electricity: 'active_energy',
                            water: 'value',
                            heat: 'value_gj'
                        },
                        decimal_separator: 'comma',
                        decimal_places: 3
                    }}
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="month"
                                label="Export Month"
                                rules={[{ required: true, message: 'Please select a month' }]}
                            >
                                <DatePicker 
                                    picker="month" 
                                    style={{ width: '100%' }}
                                    disabledDate={(date) => date.isAfter(dayjs())}
                                />
                            </Form.Item>

                            <Title level={4}>Export Preferences</Title>
                            
                            <Form.Item
                                name="decimal_separator"
                                label="Decimal Separator"
                                style={{ marginBottom: '20px' }}
                            >
                                <Radio.Group>
                                    <Radio value="comma">Comma (123,45)</Radio>
                                    <Radio value="dot">Dot (123.45)</Radio>
                                </Radio.Group>
                            </Form.Item>

                            <Form.Item
                                name="decimal_places"
                                label={
                                    <Space>
                                        <span>Decimal Places (Rounding)</span>
                                        <Tooltip title="Choose how many decimal places to round values to, or select 'No rounding' for unlimited precision.">
                                            <InfoCircleOutlined style={{ color: '#1677ff' }} />
                                        </Tooltip>
                                    </Space>
                                }
                                style={{ marginBottom: '20px' }}
                            >
                                <Select style={{ width: '100%' }} placeholder="Select decimal places">
                                    <Select.Option value={null}>No rounding (unlimited)</Select.Option>
                                    {Array.from({ length: 11 }, (_, i) => (
                                        <Select.Option key={i} value={i}>
                                            {i} decimal place{i !== 1 ? 's' : ''}{i === 3 ? ' (default)' : ''}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Text type="secondary">Select which values to export for each media type:</Text>

                            <Form.Item name={['preferences', 'electricity']} style={{ marginTop: '16px' }}>
                                <div>
                                    <Text strong>Electricity</Text>
                                    <Radio.Group style={{ display: 'block', marginTop: '8px' }} defaultValue="active_energy">
                                        <Space direction="vertical">
                                            <Radio value="active_energy">Active Energy (kWh)</Radio>
                                            <Radio value="reactive_energy">Reactive Energy (kVArh)</Radio>
                                        </Space>
                                    </Radio.Group>
                                </div>
                            </Form.Item>

                            <Form.Item name={['preferences', 'water']}>
                                <div>
                                    <Text strong>Water</Text>
                                    <Radio.Group style={{ display: 'block', marginTop: '8px' }} defaultValue="value">
                                        <Radio value="value">Volume (m³)</Radio>
                                    </Radio.Group>
                                </div>
                            </Form.Item>

                            <Form.Item name={['preferences', 'heat']}>
                                <div>
                                    <Text strong>Heat</Text>
                                    <Radio.Group style={{ display: 'block', marginTop: '8px' }} defaultValue="value_gj">
                                        <Space direction="vertical">
                                            <Radio value="value_gj">Energy (GJ)</Radio>
                                            <Radio value="value_kwh">Energy (kWh)</Radio>
                                        </Space>
                                    </Radio.Group>
                                </div>
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: '16px' }}>
                                <Text strong>How to use:</Text>
                                <ol style={{ paddingLeft: '20px', marginTop: '8px', fontSize: '14px' }}>
                                    <li>Select the month you want to export data for</li>
                                    <li>Choose which values to export for each media type</li>
                                    <li>Copy a column of meter UUIDs from your Excel file</li>
                                    <li>Paste the UUIDs in the text area below</li>
                                    <li>Click "Generate Export" to process the data</li>
                                    <li>Copy the results and paste back into Excel</li>
                                </ol>
                            </div>
                            
                            <Alert
                                message="Excel Integration Tips"
                                description={
                                    <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                                        <li>Empty lines in your UUID list will be preserved</li>
                                        <li>Results maintain the same row order as your input</li>
                                        <li>Use TSV format for best Excel compatibility</li>
                                        <li>Permission-aware: only shows data you have access to</li>
                                    </ul>
                                }
                                type="info"
                                showIcon
                            />
                        </Col>
                    </Row>

                    <Divider />

                    <Form.Item
                        name="uuids"
                        label={
                            <Space>
                                <span>Meter UUIDs</span>
                                <Tooltip title="Paste UUIDs from Excel. Empty lines will be preserved for Excel alignment.">
                                    <InfoCircleOutlined style={{ color: '#1677ff' }} />
                                </Tooltip>
                            </Space>
                        }
                        rules={[{ required: true, message: 'Please enter at least one UUID' }]}
                    >
                        <TextArea
                            rows={10}
                            placeholder={`Paste your meter UUIDs here, one per line:

V1StGXR8_Z5jdHi6B-myT
A2NqHJR9_Y6keLj7C-nzU
B3OrIKS0_X7mfMk8D-ozV

Empty lines will be preserved for Excel alignment!`}
                            onChange={handleUuidChange}
                            onPaste={handlePaste}
                        />
                    </Form.Item>

                    {parsedUuidCount > 0 && (
                        <Alert
                            message={`${parsedUuidCount} UUIDs detected`}
                            type="info"
                            showIcon
                            style={{ marginBottom: '16px' }}
                        />
                    )}

                    <Button type="primary" htmlType="submit" loading={loading} block size="large">
                        Generate Export
                    </Button>
                </Form>
            </Card>
        </div>
    );
};